import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Tag } from "primereact/tag";
import { useState } from "react";
import styled from "styled-components";

import { reorder } from "../mapper";
import { IgnoreKeywordsBox, NeverChip } from "./IgnoreKeywordsBox";
import { SelectionRuleDialog } from "./SelectionRuleDialog";
import {
  DEFAULT_RULE_TEXT,
  FOOTER_TEXT,
  ISelectionRuleForm,
  ISelectionRulesForm,
  OTHERWISE_TEXT,
  RuleScope,
  emptyRule,
  lineRoleLabel,
  networkLabel,
  orderRules,
  sourceLabel,
  tieBreakLabel,
} from "./selectionRules";

const RuleCard = styled.div<{ $default: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.125rem;
  border: 1px solid var(--surface-200);
  border-radius: 10px;
  background: ${({ $default }) =>
    $default ? "var(--surface-50)" : "var(--surface-0)"};
`;

/** The priorities table. A fifth column (Service line) only on the physician copay step. */
const Row = styled.div<{ $physician: boolean }>`
  display: grid;
  grid-template-columns: ${({ $physician }) =>
    $physician
      ? "4rem 12rem minmax(0, 1.7fr) minmax(0, 1.2fr) 8rem"
      : "4rem minmax(0, 1.7fr) minmax(0, 1.2fr) 8rem"};
  gap: 0.875rem;
  align-items: start;
  padding: 0.25rem 0;
`;

const HeaderCell = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const OtherwiseCell = styled.div<{ $physician: boolean }>`
  grid-column: span ${({ $physician }) => ($physician ? 4 : 3)};
  color: var(--text-color-secondary);
`;

const UseChip = styled.span`
  font-family: monospace;
  font-size: 0.8125rem;
  padding: 0.1875rem 0.5rem;
  border-radius: 6px;
  background: #e6f4ea;
  color: #14532d;
`;

const TypeChip = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  padding: 0.1875rem 0.5rem;
  border-radius: 6px;
  background: #e0e7ff;
  color: #312e81;
`;

function Dash() {
  return <span className="text-color-secondary">—</span>;
}

/** Each step's opening sentence. The physician one also says the ignore list from step 2 still applies. */
const INTRO_TEXT: Record<RuleScope, string> = {
  shared:
    "Set how the benefit is chosen for each payer and plan. The first rule that matches the payer and plan is the only one used: its priorities are tried in order, and the first one that finds a line sets the value. Payers no rule covers use Default.",
  professional:
    "Physician copay has its own rules because it reads a different line — the surgeon's, on the Surgical or MRI/CT service line — and should come back blank rather than borrow a facility line. The first rule that matches the payer and plan is the only one used, and the words ignored in step 2 are ignored here too.",
};

interface ISelectionRulesStepProps {
  accountId: string;
  /** Which list this step edits: `shared` is step 2, `professional` is step 3. */
  scope: RuleScope;
  value: ISelectionRulesForm;
  canEdit: boolean;
  payerOptions: Array<{ label: string; value: number }>;
  loadingPayers: boolean;
  knownRoles: string[];
  onChange: (next: ISelectionRulesForm) => void;
}

/**
 * CLE-2802 — one list of selection rules, replacing "Filter the response" and "Line priority" while
 * the API's `BENEFIT_SELECTION_RULES` flag is ON. Rendered twice by the card: "2 · Selection rules"
 * for copay, co-insurance, deductible and out-of-pocket (`scope: "shared"`, which also owns the
 * account-wide ignore list), and "3 · Physician copay rules" (`scope: "professional"`) only while the
 * account bills a physician copay — an account that does not has no step 3 at all, rather than a tab
 * of rules that do nothing (Mike, 2026-09-30). Built to the design Mike and Ani signed off on
 * 2026-09-29; its words are used verbatim.
 *
 * 🚨 NO RULE NUMBERS, ANYWHERE. A rule is named by its payers and plans; a number would read as a
 * priority between rules, and the only ordering that matters is "the first rule that covers the
 * payer and plan is the only one used", which the intro says.
 *
 * Default is always last, cannot be deleted or moved, and can be edited (its priorities and network).
 */
export function SelectionRulesStep({
  accountId,
  scope,
  value,
  canEdit,
  payerOptions,
  loadingPayers,
  knownRoles,
  onChange,
}: ISelectionRulesStepProps) {
  /** Index into this step's rules, "new" when adding, null when closed. */
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [newRule, setNewRule] = useState<ISelectionRuleForm>(() =>
    emptyRule(scope)
  );

  const rules = value[scope];
  const isPhysician = scope === "professional";

  const setRules = (next: ISelectionRuleForm[]) =>
    onChange({ ...value, [scope]: orderRules(next) });

  const payerName = (id: number) =>
    payerOptions.find((option) => option.value === id)?.label ?? `Payer #${id}`;

  const nonDefaultCount = rules.filter((rule) => !rule.isDefault).length;

  const confirmRemove = (index: number) =>
    confirmDialog({
      message: "Remove this rule? Removed on save.",
      header: "Remove rule",
      style: { width: "min(520px, 95vw)" },
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Remove",
      rejectLabel: "Cancel",
      accept: () => setRules(rules.filter((_, i) => i !== index)),
    });

  const handleSave = (rule: ISelectionRuleForm) => {
    if (editing === "new") {
      // A NEW RULE GOES TO THE TOP, as a new payer filter does: the first rule covering a payer is
      // the only one used, so one added below an existing rule for the same payer would never run.
      setRules([rule, ...rules]);
    } else if (editing !== null) {
      setRules(rules.map((current, i) => (i === editing ? rule : current)));
    }
    setEditing(null);
  };

  const plansText = (rule: ISelectionRuleForm) => {
    if (rule.isDefault) return DEFAULT_RULE_TEXT;
    if (rule.planKeywords.length)
      return `Plan name contains ${rule.planKeywords.join(", ")}`;
    return "All plans";
  };

  const hasDefault = rules.some((rule) => rule.isDefault);

  return (
    <div
      className="flex flex-column gap-4"
      data-testid={`selectionRules.${scope}`}
    >
      <span className="text-color-secondary" style={{ maxWidth: "70rem" }}>
        {INTRO_TEXT[scope]}
      </span>

      {!isPhysician && (
        <IgnoreKeywordsBox
          accountId={accountId}
          value={value.ignoreKeywords}
          disabled={!canEdit}
          onChange={(ignoreKeywords) => onChange({ ...value, ignoreKeywords })}
        />
      )}

      {/*
        Capped width: on a wide screen a full-width card is mostly empty space between the payer
        chips and the buttons. Chips and keyword cells wrap, so a long list takes more rows, not more
        width. The Add buttons, the cards and the footer share the cap so their right edges line up.
      */}
      <div className="flex flex-column gap-3" style={{ maxWidth: "72rem" }}>
        {canEdit && (
          <div className="flex justify-content-end gap-2">
            {/*
              Only while the step has no Default: "+ Add rule" makes a scoped rule, which needs a
              payer or a plan, so without this an account with no catch-all could get one only
              through SQL. It lands last through `orderRules`, like every Default.
            */}
            {!hasDefault && (
              <Button
                type="button"
                label="Add Default"
                className="p-button-outlined p-button-sm"
                data-testid={`selectionRules.${scope}.addDefault`}
                onClick={() => {
                  setNewRule({ ...emptyRule(scope), isDefault: true });
                  setEditing("new");
                }}
              />
            )}
            <Button
              type="button"
              label="+ Add rule"
              className="p-button-outlined p-button-sm"
              data-testid={`selectionRules.${scope}.addRule`}
              onClick={() => {
                setNewRule(emptyRule(scope));
                setEditing("new");
              }}
            />
          </div>
        )}

        {rules.map((rule, index) => {
          const testId = `selectionRules.${scope}.rule.${
            rule.isDefault ? "default" : index
          }`;
          return (
            <RuleCard
              key={rule.key}
              $default={rule.isDefault}
              data-testid={testId}
            >
              <div className="flex gap-3 align-items-start">
                <div className="flex flex-column gap-2 flex-grow-1">
                  <div
                    className="flex flex-wrap gap-2"
                    data-testid={`${testId}.payers`}
                  >
                    {rule.isDefault ? (
                      <Tag value="Default" className="surface-200 text-color" />
                    ) : (
                      rule.payerIds.map((id) => (
                        <Tag
                          key={id}
                          value={payerName(id)}
                          className="surface-200 text-color"
                        />
                      ))
                    )}
                  </div>
                  <span
                    className="text-xs text-color-secondary"
                    data-testid={`${testId}.plans`}
                  >
                    {plansText(rule)}
                  </span>
                  <div data-testid={`${testId}.network`}>
                    <Tag
                      value={networkLabel(rule.networkStatus)}
                      severity={
                        rule.networkStatus === "OutOfNetwork"
                          ? "warning"
                          : "success"
                      }
                    />
                  </div>
                </div>
                {canEdit && (
                  <div className="flex gap-1 align-items-center">
                    {!rule.isDefault && nonDefaultCount > 1 && (
                      <>
                        <Button
                          type="button"
                          icon="pi pi-chevron-up"
                          className="p-button-text p-button-sm"
                          aria-label="Move rule up"
                          disabled={index === 0}
                          data-testid={`${testId}.up`}
                          onClick={() =>
                            setRules(reorder(rules, index, index - 1))
                          }
                        />
                        <Button
                          type="button"
                          icon="pi pi-chevron-down"
                          className="p-button-text p-button-sm"
                          aria-label="Move rule down"
                          disabled={index >= nonDefaultCount - 1}
                          data-testid={`${testId}.down`}
                          onClick={() =>
                            setRules(reorder(rules, index, index + 1))
                          }
                        />
                      </>
                    )}
                    <Button
                      type="button"
                      label="Edit"
                      className="p-button-outlined p-button-sm"
                      aria-label={rule.isDefault ? "Edit Default" : "Edit rule"}
                      data-testid={`${testId}.edit`}
                      onClick={() => setEditing(index)}
                    />
                    {!rule.isDefault && (
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-text p-button-sm p-button-danger"
                        aria-label="Remove rule"
                        data-testid={`${testId}.remove`}
                        onClick={() => confirmRemove(index)}
                      />
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-column gap-2 pt-2 border-top-1 surface-border">
                <Row $physician={isPhysician}>
                  <HeaderCell>Priority</HeaderCell>
                  {isPhysician && <HeaderCell>Service line</HeaderCell>}
                  <HeaderCell>Contains any of</HeaderCell>
                  <HeaderCell>Doesn&apos;t contain</HeaderCell>
                  <HeaderCell>If several</HeaderCell>
                </Row>
                {rule.priorities.map((priority, j) => (
                  <Row
                    // Priorities have no identity beyond their position.
                    // eslint-disable-next-line react/no-array-index-key
                    key={j}
                    $physician={isPhysician}
                    data-testid={`${testId}.priority.${j}`}
                  >
                    <span className="font-bold text-primary">{j + 1}</span>
                    {isPhysician && <span>{sourceLabel(priority.source)}</span>}
                    <div className="flex flex-wrap gap-2">
                      {isPhysician && priority.lineRole && (
                        <TypeChip>{`Line type: ${lineRoleLabel(
                          priority.lineRole
                        )}`}</TypeChip>
                      )}
                      {priority.matchKeywords.map((word) => (
                        <UseChip key={word}>{word}</UseChip>
                      ))}
                      {priority.matchKeywords.length === 0 &&
                        !(isPhysician && priority.lineRole) && <Dash />}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {priority.excludeKeywords.map((word) => (
                        <NeverChip key={word}>{word}</NeverChip>
                      ))}
                      {priority.excludeKeywords.length === 0 && <Dash />}
                    </div>
                    <span>{tieBreakLabel(priority.tieBreak)}</span>
                  </Row>
                ))}
                <Row $physician={isPhysician}>
                  <span className="text-xs font-semibold text-color-secondary">
                    Otherwise
                  </span>
                  <OtherwiseCell
                    $physician={isPhysician}
                    data-testid={`${testId}.otherwise`}
                  >
                    {OTHERWISE_TEXT[scope]}
                  </OtherwiseCell>
                </Row>
              </div>
            </RuleCard>
          );
        })}

        {!hasDefault && (
          <span
            className="text-color-secondary"
            data-testid={`selectionRules.${scope}.noDefault`}
          >
            No Default: payers and plans no rule above covers are read with no
            rule, so every line is considered. Use Add Default to choose how
            they are read.
          </span>
        )}

        <span className="text-color-secondary pt-1">{FOOTER_TEXT[scope]}</span>
      </div>

      <SelectionRuleDialog
        visible={editing !== null}
        scope={scope}
        isNew={editing === "new"}
        value={
          editing === "new" || editing === null
            ? newRule
            : rules[editing] ?? newRule
        }
        payerOptions={payerOptions}
        loadingPayers={loadingPayers}
        knownRoles={knownRoles}
        onSave={handleSave}
        onHide={() => setEditing(null)}
      />
    </div>
  );
}
